import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { modalMap, stepDataMap } from './data'
import { planStepIndexFromModalQueryParam } from './playbookHelpers'

const usePlaybookModal = ({ isSingleSite, showMonitorViewOnly }) => {
  const [currentPlanStep, setCurrentPlanStep] = useState(0)
  const [goalsOnlyMode, setGoalsOnlyMode] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [ModalComponent, setModalComponent] = useState(null)

  const location = useLocation()
  const handleModalIdRef = useRef(null)
  const modalId = useMemo(() => {
    const searchParams = new URLSearchParams(location?.search || '')
    return searchParams.get('modalId')
  }, [location?.search])

  const launchModal = useCallback((modalName, { goalsOnly = false } = {}) => {
    const Modal = modalMap[modalName]?.component
    if (Modal) {
      setGoalsOnlyMode(goalsOnly)
      setModalComponent(() => Modal)
      setIsModalOpen(true)
    }
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setModalComponent(null)
    setGoalsOnlyMode(false)
  }, [])

  const openModalAtStep = useCallback(
    (stepIndex, modalName) => {
      setCurrentPlanStep(stepIndex)
      launchModal(modalName ?? stepDataMap[stepIndex]?.modalName)
    },
    [launchModal],
  )

  const handleModalClose = useCallback(() => {
    const nextStepIndex = currentPlanStep + 1
    if (nextStepIndex < Object.keys(stepDataMap).length) {
      setCurrentPlanStep(nextStepIndex)
      launchModal(stepDataMap[nextStepIndex].modalName)
      return
    }

    closeModal()
  }, [closeModal, currentPlanStep, launchModal])

  useEffect(() => {
    if (!showMonitorViewOnly || !isSingleSite || !modalId) {
      if (!modalId) {
        handleModalIdRef.current = null
      }
      return
    }

    if (handleModalIdRef.current === modalId) return

    handleModalIdRef.current = modalId
    const stepFromQuery = planStepIndexFromModalQueryParam(modalId)
    if (stepFromQuery !== null) {
      setCurrentPlanStep(stepFromQuery)
    }
    launchModal(modalId)
  }, [isSingleSite, launchModal, modalId, showMonitorViewOnly])

  return {
    ModalComponent,
    closeModal,
    currentPlanStep,
    goalsOnlyMode,
    handleModalClose,
    isModalOpen,
    launchModal,
    openModalAtStep,
    setCurrentPlanStep,
  }
}

export default usePlaybookModal
